// Certificate generator shared by CrossReg Training (/training) and CrossRegGuard's Training page.
// Every certificate carries the signature of the authorising officer.
import { CERT_SIGNATORY, CERT_SIGNATURE_DATA_URI } from './certificateSignature';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// Date-only values ("2026-11-04") are read as local dates so they don't shift a day in US time zones
const fmtDate = (d) => {
  const v = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00` : (d || Date.now());
  return new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * Opens a printable A4 landscape certificate.
 * @param {string} rawTitle       course / module title
 * @param {string} rawName        recipient name
 * @param {string} completedAt    completion date
 * @param {string} rawCompany     recipient's company
 * @param {string} certificateCode verifiable code (CRG-XXXX-…) when the certificate is recorded
 * @param {object} opts           { brand, subtitle, kind, score, expiresAt }
 */
export function downloadTrainingCertificate(rawTitle, rawName, completedAt, rawCompany = 'CrossReg Nexus', certificateCode = null, opts = {}) {
  const trainingTitle = esc(rawTitle);
  const userName = esc(rawName);
  const companyName = esc(rawCompany);
  const brand = esc(opts.brand || 'CrossReg Training');
  const subtitle = esc(opts.subtitle || 'COMPLIANCE TRAINING PLATFORM · BY CROSSREG NEXUS');
  const kind = esc(opts.kind || 'the compliance training module');
  const date = fmtDate(completedAt);
  const certId = esc(certificateCode || opts.certIdLabel || 'Not yet recorded');
  const verify = certificateCode ? `Certificate ID: ${certId}<br/>Verifiable with CrossReg Nexus` : `Certificate ID: ${certId}`;
  const extra = [
    opts.score != null && opts.score !== '' ? `Score: ${esc(opts.score)}%` : '',
    opts.expiresAt ? `Valid until ${esc(fmtDate(opts.expiresAt))}` : '',
  ].filter(Boolean).join(' · ');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Certificate — ${trainingTitle}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Mono:wght@300;400;500&display=swap');
  @page{size:A4 landscape;margin:0}
  *{margin:0;padding:0;box-sizing:border-box}
  body{width:297mm;height:210mm;background:#fff;display:flex;align-items:center;justify-content:center;font-family:'DM Mono',monospace}
  .cert{width:100%;height:100%;padding:16mm 20mm 14mm;position:relative;border:12px solid #0A2342;display:flex;flex-direction:column;align-items:center;text-align:center}
  .main{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%}
  .cert::before{content:'';position:absolute;inset:8px;border:2px solid #00D4FF;pointer-events:none}
  .logo{display:flex;align-items:center;gap:10px;margin-bottom:5mm}
  .logo-hex{width:32px;height:32px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)}
  .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:22px;color:#0A2342;letter-spacing:0.04em}
  .subtitle{font-size:9px;letter-spacing:0.2em;color:#4A5568;margin-bottom:5mm}
  .cert-title{font-family:'Syne',sans-serif;font-size:11px;letter-spacing:0.25em;color:#00A6C8;margin-bottom:4mm}
  .completion{font-family:'Syne',sans-serif;font-size:30px;font-weight:800;color:#0A2342;margin-bottom:4mm;line-height:1.2}
  .presented{font-size:11px;color:#4A5568;margin-bottom:3mm}
  .name{font-family:'Syne',sans-serif;font-size:26px;font-weight:700;color:#0A2342;border-bottom:2px solid #00D4FF;padding-bottom:3mm;margin-bottom:3mm;min-width:120mm}
  .company{font-size:11px;color:#4A5568;margin-bottom:5mm}
  .module{font-size:10px;color:#4A5568;margin-bottom:2mm}
  .module-name{font-family:'Syne',sans-serif;font-size:16px;font-weight:700;color:#0A2342;margin-bottom:2mm}
  .extra{font-size:9px;color:#4A5568;margin-bottom:4mm;letter-spacing:0.06em}
  .footer{display:flex;justify-content:space-between;width:100%;align-items:flex-end;gap:10mm}
  .sig-block{text-align:center;width:72mm}
  .sig-img{height:17mm;max-width:66mm;object-fit:contain;display:block;margin:0 auto -2mm}
  .sig-line{width:62mm;border-top:1px solid #0A2342;margin:0 auto 2mm}
  .sig-label{font-size:9px;color:#4A5568;letter-spacing:0.1em}
  .sig-strong{font-size:10px;font-weight:700;color:#0A2342;letter-spacing:0.04em;margin-top:1mm}
  .sig-sub{font-size:8px;color:#4A5568;margin-top:0.5mm}
  .cert-id{font-size:8px;color:#8A9BB5;line-height:1.6;white-space:nowrap;padding-bottom:2mm}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body><div class="cert"><div class="main">
  <div class="logo"><div class="logo-hex"></div><span class="logo-text">${brand}</span></div>
  <div class="subtitle">${subtitle}</div>
  <div class="cert-title">CERTIFICATE OF COMPLETION</div>
  <div class="completion">This certifies that</div>
  <div class="presented">the following individual has successfully completed</div>
  <div class="name">${userName}</div>
  <div class="company">${companyName}</div>
  <div class="module">${kind}</div>
  <div class="module-name">${trainingTitle}</div>
  ${extra ? `<div class="extra">${extra}</div>` : ''}
  </div>
  <div class="footer">
    <div class="sig-block"><div class="sig-strong" style="font-size:13px;margin-bottom:2mm">${esc(date)}</div><div class="sig-line"></div><div class="sig-label">DATE OF COMPLETION</div><div class="sig-sub">&nbsp;</div><div class="sig-sub">&nbsp;</div></div>
    <div class="cert-id">${verify}</div>
    <div class="sig-block">
      <img class="sig-img" src="${CERT_SIGNATURE_DATA_URI}" alt="Signature of ${esc(CERT_SIGNATORY.name)}" />
      <div class="sig-line"></div>
      <div class="sig-label">AUTHORISED BY</div>
      <div class="sig-strong">${esc(CERT_SIGNATORY.name)}, ${esc(CERT_SIGNATORY.credentials)}</div>
      <div class="sig-sub">${esc(CERT_SIGNATORY.title)}</div>
    </div>
  </div>
</div></body></html>`;

  const w = opts.win && !opts.win.closed ? opts.win : window.open('', '_blank');
  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Print once the signature image has decoded
    const img = w.document.querySelector('.sig-img');
    const go = () => setTimeout(() => w.print(), 400);
    if (img && !img.complete) { img.onload = go; img.onerror = go; } else go();
  }
}

/**
 * Finds the learner's recorded certificate for a completed training, issuing it if it does not exist
 * yet (e.g. completed on another device, or before certificates were recorded).
 */
export async function getOrIssueCertificate(moduleId, certificateName) {
  try {
    const list = await fetch('/api/training/certificates', { credentials: 'include' }).then(r => (r.ok ? r.json() : []));
    const found = (Array.isArray(list) ? list : []).find(c => c.training_id === moduleId);
    if (found) return found;
    const r = await fetch('/api/training/create-training-certificate', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ moduleId, certificateName: certificateName || undefined }),
    });
    return r.ok ? await r.json() : null;
  } catch {
    return null;
  }
}

/**
 * Download button handler for a completed training: opens the window straight away (so pop-up
 * blockers allow it), then fills it with the recorded certificate — code, recipient and date.
 */
export async function downloadRecordedCertificate({ moduleId, title, completedAt, fallbackName, company = 'CrossReg Nexus', brand, subtitle }) {
  const win = window.open('', '_blank');
  if (win) win.document.write('<p style="font-family:sans-serif;padding:24px">Preparing your certificate…</p>');
  const cert = await getOrIssueCertificate(moduleId, fallbackName);
  downloadTrainingCertificate(
    cert?.training_title || title,
    cert?.recipient_name || fallbackName,
    cert?.issued_at || completedAt,
    company,
    cert?.code || null,
    { win, brand, subtitle, score: cert?.score ?? undefined, expiresAt: cert?.expires_at || undefined },
  );
}
